import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  FormControlLabel,
  FormGroup,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
} from "@mui/material";
import SideDrawer from "../../common/side-drawer";
import ButtonOne from "../../common/normal-button";
import {
  liveAgentTextFieldSx as textFieldSx,
  NotificationUsersSelect,
  NotificationTemplateSelect,
  EndChatFields,
  QueueCleanupFields,
} from "../../common/live-agent-settings-fields";
import { selectFieldSx, labelSx, sectionLabelSx } from "./utils";
import { ColorsConstants, borderColors } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import {
  NODEBOT_FALLBACK_AUTORESPONDERS,
  USERS_ENDPOINT,
  TEMPLATE_ENDPOINT,
} from "../../config/urls";

// Letters, digits, spaces, hyphens and underscores only, with at least one letter or digit —
// rejects names like '""' that are non-empty but made up purely of symbols.
const VALID_NAME_REGEX = /^(?=.*[\p{L}\p{N}])[\p{L}\p{N} _-]+$/u;

const TYPE_OPTIONS = [
  { value: "message", label: "Message" },
  { value: "live_agent", label: "Live Agent" },
];

const LIVE_AGENT_TRIGGER_TYPE_OPTIONS = [
  { value: "IMMEDIATE", label: "Immediate" },
  { value: "AUTO_RESPONSE", label: "Auto Response" },
];

const FallbackAutoresponderEdit = (props) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const autoresponderId = props.pageParams?.autoresponderId;

  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const [name, setName] = useState(props.pageParams?.autoresponderName || "");
  const [type, setType] = useState("message");
  const [message, setMessage] = useState("");
  const [liveAgentTriggerType, setLiveAgentTriggerType] = useState("IMMEDIATE");
  const [liveAgentInitialMessage, setLiveAgentInitialMessage] = useState("");

  // On end chat by support agent
  const [endChatMessageMode, setEndChatMessageMode] = useState("SEND_MESSAGE");
  const [endChatMessage, setEndChatMessage] = useState(
    "Thank you for contacting us! Hope we were able to help. Have a great day \u{1F60A}",
  );

  // Unassigned queue cleanup — opt-in, disabled by default.
  const [queueCleanupEnabled, setQueueCleanupEnabled] = useState(false);
  const [queueCleanupHours, setQueueCleanupHours] = useState(12);

  // Agent Connection Notifications — per-autoresponder, not tenant-wide.
  const [usersData, setUsersData] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [notifications, setNotifications] = useState({ email: false, whatsapp: false });
  const [emailUsers, setEmailUsers] = useState([]);
  const [whatsappUsers, setWhatsappUsers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Snapshot of the editable fields as last loaded from the server — compared against
  // current state to report `onDirtyChange`.
  const initialSnapshotRef = useRef(null);

  const buildSnapshot = () =>
    JSON.stringify({
      name,
      type,
      message,
      liveAgentTriggerType,
      liveAgentInitialMessage,
      notifications,
      emailUserIds: emailUsers.map((u) => u.id).sort(),
      whatsappUserIds: whatsappUsers.map((u) => u.id).sort(),
      templateId: selectedTemplate?.template_id || null,
      endChatMessageMode,
      endChatMessage,
      queueCleanupEnabled,
      queueCleanupHours,
    });

  const fetchUsers = async (token, headers) => {
    const response = await fetch(USERS_ENDPOINT, { headers });
    if (!response.ok) return [];
    const data = await response.json();
    return data.results || [];
  };

  const fetchTemplates = async (headers) => {
    const response = await fetch(
      `${TEMPLATE_ENDPOINT}?category_filter=utility&limit=1000&ordering=-last_edited`,
      { headers },
    );
    if (!response.ok) return [];
    const data = await response.json();
    return (data.results || []).filter((t) => t.active && t.status === "APPROVED");
  };

  // Re-fetches every time the drawer opens (not just when the id changes) so reopening
  // the same autoresponder after an abandoned, unsaved edit shows fresh server data
  // instead of the stale in-memory draft.
  useEffect(() => {
    if (!props.open) return;
    // Drop validation errors left over from an earlier, abandoned edit session
    setErrors({});
    setSnackbar((prev) => ({ ...prev, open: false }));
    if (!autoresponderId) {
      setFetchError("No autoresponder ID provided.");
      setFetchLoading(false);
      return;
    }
    const load = async () => {
      setFetchLoading(true);
      setFetchError("");
      try {
        const token = await getValidToken();
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        };

        const [users, templatesList, response] = await Promise.all([
          fetchUsers(token, headers),
          fetchTemplates(headers),
          fetch(`${NODEBOT_FALLBACK_AUTORESPONDERS}?limit=200`, { headers }),
        ]);
        setUsersData(users);
        setTemplates(templatesList);

        if (!response.ok) throw new Error("Failed to load fallback autoresponder");
        const data = await response.json();
        const responder = (data.results || []).find(
          (r) => String(r.id) === String(autoresponderId),
        );
        if (!responder) throw new Error("Fallback autoresponder not found");

        const responderEmailUsers = users.filter((u) =>
          (responder.email_notification_users || []).includes(u.id),
        );
        const responderWhatsappUsers = users.filter((u) =>
          (responder.whatsapp_notification_users || []).includes(u.id),
        );
        const responderTemplate = responder.whatsapp_template
          ? templatesList.find((t) => t.template_id === responder.whatsapp_template) || null
          : null;

        setName(responder.name || "");
        setType(responder.type || "message");
        setMessage(responder.message || "");
        setLiveAgentTriggerType(responder.live_agent_trigger_type || "IMMEDIATE");
        setLiveAgentInitialMessage(responder.live_agent_initial_message || "");
        setNotifications({
          email: responderEmailUsers.length > 0,
          whatsapp: responderWhatsappUsers.length > 0,
        });
        setEmailUsers(responderEmailUsers);
        setWhatsappUsers(responderWhatsappUsers);
        setSelectedTemplate(responderTemplate);
        setEndChatMessageMode(responder.end_chat_message_mode || "SEND_MESSAGE");
        setEndChatMessage(
          responder.end_chat_message ||
            "Thank you for contacting us! Hope we were able to help. Have a great day \u{1F60A}",
        );
        setQueueCleanupEnabled(!!responder.unassigned_queue_cleanup_enabled);
        setQueueCleanupHours(responder.unassigned_queue_cleanup_hours || 12);

        initialSnapshotRef.current = JSON.stringify({
          name: responder.name || "",
          type: responder.type || "message",
          message: responder.message || "",
          liveAgentTriggerType: responder.live_agent_trigger_type || "IMMEDIATE",
          liveAgentInitialMessage: responder.live_agent_initial_message || "",
          notifications: {
            email: responderEmailUsers.length > 0,
            whatsapp: responderWhatsappUsers.length > 0,
          },
          emailUserIds: responderEmailUsers.map((u) => u.id).sort(),
          whatsappUserIds: responderWhatsappUsers.map((u) => u.id).sort(),
          templateId: responderTemplate?.template_id || null,
          endChatMessageMode: responder.end_chat_message_mode || "SEND_MESSAGE",
          endChatMessage:
            responder.end_chat_message ||
            "Thank you for contacting us! Hope we were able to help. Have a great day \u{1F60A}",
          queueCleanupEnabled: !!responder.unassigned_queue_cleanup_enabled,
          queueCleanupHours: responder.unassigned_queue_cleanup_hours || 12,
        });
      } catch (err) {
        setFetchError(err.message || "Failed to load fallback autoresponder");
      } finally {
        setFetchLoading(false);
      }
    };
    load();
  }, [autoresponderId, props.open]);

  // Reports unsaved-changes state up to the Workflows list so it can warn before
  // switching tabs (which would otherwise silently discard this drawer's edits).
  useEffect(() => {
    if (!props.onDirtyChange) return;
    if (!props.open || fetchLoading || !initialSnapshotRef.current) {
      props.onDirtyChange(false);
      return;
    }
    props.onDirtyChange(buildSnapshot() !== initialSnapshotRef.current);
  }, [
    props.open,
    fetchLoading,
    name,
    type,
    message,
    liveAgentTriggerType,
    liveAgentInitialMessage,
    notifications,
    emailUsers,
    whatsappUsers,
    selectedTemplate,
    endChatMessageMode,
    endChatMessage,
    queueCleanupEnabled,
    queueCleanupHours,
  ]);

  const handleClose = () => {
    props.onClose && props.onClose();
  };

  // Cancel / X: warn first if the form differs from what was last loaded from the server
  const handleCancel = () => {
    const isDirty =
      !!initialSnapshotRef.current && buildSnapshot() !== initialSnapshotRef.current;
    if (isDirty && !window.confirm("You have unsaved changes. Do you want to leave without saving?")) {
      return;
    }
    handleClose();
  };

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = "Name is required";
    else if (!VALID_NAME_REGEX.test(name.trim()))
      errs.name = "Name can only contain letters, numbers, spaces, hyphens and underscores";
    if (type === "message" && !message.trim()) errs.message = "Message is required";
    if (
      type === "live_agent" &&
      liveAgentTriggerType === "AUTO_RESPONSE" &&
      !liveAgentInitialMessage.trim()
    ) {
      errs.liveAgentInitialMessage = "Initial message is required for Auto Response";
    }
    if (type === "live_agent" && notifications.whatsapp) {
      if (whatsappUsers.length === 0) {
        errs.whatsappUsers = "Select at least one WhatsApp user for notifications";
      } else if (!selectedTemplate) {
        errs.whatsappTemplate = "Select a WhatsApp template for notifications";
      } else {
        const matches = selectedTemplate?.template_json?.content?.match(/{{\d+}}/g) || [];
        if (new Set(matches).size !== 1) {
          errs.whatsappTemplate = "Selected template must have exactly 1 variable (e.g. {{1}})";
        }
      }
    }
    if (
      type === "live_agent" &&
      endChatMessageMode === "SEND_MESSAGE" &&
      !endChatMessage.trim()
    ) {
      errs.endChatMessage = "End chat message is required";
    }
    if (type === "live_agent" && queueCleanupEnabled) {
      const hours = Number(queueCleanupHours);
      if (!Number.isInteger(hours) || hours < 1 || hours > 168) {
        errs.queueCleanupHours = "Enter a whole number of hours between 1 and 168";
      }
    }
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSaving(true);

    try {
      const token = await getValidToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const isLiveAgent = type === "live_agent";
      const payload = {
        name: name.trim(),
        type,
        message: type === "message" ? message.trim() : "",
        live_agent_trigger_type: liveAgentTriggerType,
        live_agent_initial_message:
          isLiveAgent && liveAgentTriggerType === "AUTO_RESPONSE"
            ? liveAgentInitialMessage.trim() || null
            : null,
        email_notification_users:
          isLiveAgent && notifications.email ? emailUsers.map((u) => u.id) : [],
        whatsapp_notification_users:
          isLiveAgent && notifications.whatsapp ? whatsappUsers.map((u) => u.id) : [],
        whatsapp_template:
          isLiveAgent && notifications.whatsapp ? selectedTemplate?.template_id : null,
        end_chat_message_mode: isLiveAgent ? endChatMessageMode : "SEND_MESSAGE",
        end_chat_message:
          isLiveAgent && endChatMessageMode === "SEND_MESSAGE" ? endChatMessage.trim() : "",
        unassigned_queue_cleanup_enabled: isLiveAgent ? queueCleanupEnabled : false,
        unassigned_queue_cleanup_hours: Number(queueCleanupHours) || 12,
      };

      const response = await fetch(`${NODEBOT_FALLBACK_AUTORESPONDERS}${autoresponderId}/`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || "Failed to save fallback autoresponder");
      }

      setSnackbar({ open: true, message: "Fallback autoresponder saved successfully", severity: "success" });
      props.onSaved && props.onSaved();
      setTimeout(() => handleClose(), 1000);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to save fallback autoresponder",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };


  return (
    <SideDrawer
      open={!!props.open}
      onClose={handleCancel}
      title="Edit Fallback Autoresponder"
      subtitle={name || "Untitled Autoresponder"}
    >
      {fetchLoading ? (
        <Box
          sx={{
            height: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryGray40 }}>
            Loading fallback autoresponder...
          </Typography>
        </Box>
      ) : (
        <>
      {fetchError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: "8px" }}>
          {fetchError}
        </Alert>
      )}

      {/* Form Card */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          p: 3,
        }}
      >
        <Typography sx={sectionLabelSx}>Autoresponder Content</Typography>

        <Box sx={{ mb: 2.5 }}>
          <Typography sx={labelSx}>Name</Typography>
          <TextField
            fullWidth
            placeholder="e.g. Default"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            error={!!errors.name}
            helperText={errors.name}
            sx={textFieldSx}
          />
        </Box>

        <Box sx={{ mb: 2.5, maxWidth: 280 }}>
          <Typography sx={labelSx}>Type</Typography>
          <FormControl fullWidth size="small">
            <Select value={type} onChange={(e) => setType(e.target.value)} sx={selectFieldSx}>
              {TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ ...typography.bodyLarge }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", mt: 0.5 }}>
            {type === "message"
              ? "Replies with the message below — no live agent involvement."
              : "Skips the message entirely and goes straight to the live agent flow."}
          </Typography>
        </Box>

        {type === "message" && (
          <Box sx={{ mb: 1 }}>
            <Typography sx={labelSx}>Message</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="e.g. Thank you for contacting us, our team will get back to you soon."
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                setErrors((prev) => ({ ...prev, message: undefined }));
              }}
              error={!!errors.message}
              helperText={errors.message}
              sx={textFieldSx}
            />
          </Box>
        )}

        {type === "live_agent" && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Typography sx={sectionLabelSx}>Live Agent Configuration</Typography>

            <Box sx={{ maxWidth: 280, mb: 2.5 }}>
              <Typography sx={labelSx}>Trigger Type</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={liveAgentTriggerType}
                  onChange={(e) => setLiveAgentTriggerType(e.target.value)}
                  sx={selectFieldSx}
                >
                  {LIVE_AGENT_TRIGGER_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ ...typography.bodyLarge }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {liveAgentTriggerType === "AUTO_RESPONSE" && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={labelSx}>Initial Message</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="e.g. Thank you! A support agent will be with you shortly."
                  value={liveAgentInitialMessage}
                  onChange={(e) => {
                    setLiveAgentInitialMessage(e.target.value);
                    setErrors((prev) => ({ ...prev, liveAgentInitialMessage: undefined }));
                  }}
                  error={!!errors.liveAgentInitialMessage}
                  helperText={errors.liveAgentInitialMessage}
                  sx={textFieldSx}
                />
              </Box>
            )}

            <Divider sx={{ mb: 3 }} />
            <Typography sx={sectionLabelSx}>Agent Connection Notifications</Typography>

            <FormControl sx={{ mb: 2, width: "100%" }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notifications.email}
                      onChange={(e) =>
                        setNotifications((prev) => ({ ...prev, email: e.target.checked }))
                      }
                    />
                  }
                  label={<Typography sx={typography.bodyLarge}>Email</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notifications.whatsapp}
                      onChange={(e) =>
                        setNotifications((prev) => ({ ...prev, whatsapp: e.target.checked }))
                      }
                    />
                  }
                  label={<Typography sx={typography.bodyLarge}>WhatsApp</Typography>}
                />
              </FormGroup>
            </FormControl>

            {notifications.email && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...typography.bodySmall, mb: 1 }}>Notify via Email</Typography>
                <NotificationUsersSelect
                  kind="email"
                  users={usersData}
                  value={emailUsers}
                  onChange={setEmailUsers}
                />
              </Box>
            )}

            {notifications.whatsapp && (
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ ...typography.bodySmall, mb: 1 }}>Notify via WhatsApp</Typography>
                <NotificationUsersSelect
                  kind="whatsapp"
                  users={usersData}
                  value={whatsappUsers}
                  onChange={(newValue) => {
                    setWhatsappUsers(newValue);
                    setErrors((prev) => ({ ...prev, whatsappUsers: undefined }));
                  }}
                  error={errors.whatsappUsers}
                />

                {whatsappUsers.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <NotificationTemplateSelect
                      templates={templates}
                      value={selectedTemplate}
                      onChange={(newValue) => {
                        setSelectedTemplate(newValue);
                        setErrors((prev) => ({ ...prev, whatsappTemplate: undefined }));
                      }}
                      error={errors.whatsappTemplate}
                    />
                  </Box>
                )}
              </Box>
            )}

            <EndChatFields
              mode={endChatMessageMode}
              message={endChatMessage}
              onModeChange={setEndChatMessageMode}
              onMessageChange={(value) => {
                setEndChatMessage(value);
                setErrors((prev) => ({ ...prev, endChatMessage: undefined }));
              }}
              error={errors.endChatMessage}
            />

            <QueueCleanupFields
              enabled={queueCleanupEnabled}
              hours={queueCleanupHours}
              onEnabledChange={setQueueCleanupEnabled}
              onHoursChange={setQueueCleanupHours}
              error={errors.queueCleanupHours}
            />
          </>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
          <ButtonOne
            variant="outlined"
            onClick={handleCancel}
            disabled={saving}
            sx={{
              height: 40,
              px: 2.5,
              ...typography.bodyLarge,
              border: `1px solid ${borderColors.inputFieldGrey}`,
              color: "#1D2330",
              "&:hover": {
                border: `1px solid ${ColorsConstants.primaryGray50}`,
                backgroundColor: "transparent",
              },
            }}
          >
            Cancel
          </ButtonOne>
          <ButtonOne
            variant="contained"
            onClick={handleSave}
            disabled={saving || !!fetchError}
            startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
            sx={{
              height: 40,
              px: 2.5,
              ...typography.bodyLarge,
              backgroundColor: ColorsConstants.primaryBackgroundColor,
              "&:hover": {
                backgroundColor: ColorsConstants.primaryBackgroundColor,
                opacity: 0.9,
              },
              "&.Mui-disabled": {
                backgroundColor: ColorsConstants.primaryBackgroundColor,
                opacity: 0.6,
              },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </ButtonOne>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ bottom: { xs: 16, sm: 24 }, right: { xs: 16, sm: 24 } }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </>
      )}
    </SideDrawer>
  );
};

export default FallbackAutoresponderEdit;
